// Shared themed-modal styling for the bio app.
//
// Every modal/popup that pops on top of a bio page (AccountModal,
// CollabModal, SponsorInquiryModal, PodcastModal, the inline order
// download overlay, …) should pull its accent color from the talent's
// `bio_settings.button_color` so the popup *feels* like an extension
// of their page rather than a generic ShoutOut sheet.
//
// This module exposes:
//   - parseColor / rgbaWithAlpha : tiny color utils (no deps)
//   - getModalTheme(accentHex)   : returns ready-to-spread style objects
//                                  for the backdrop, the modal shell,
//                                  CTA buttons, inputs, and check chips.
//
// Keep this file pure (no React, no DOM access) so it tree-shakes well
// and stays usable from class components / non-component code.

export interface RGB { r: number; g: number; b: number; }

const FALLBACK_RGB: RGB = { r: 59, g: 130, b: 246 }; // #3b82f6

export const parseColor = (input?: string): RGB => {
  if (!input) return FALLBACK_RGB;
  const h = input.replace('#', '').trim();
  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  if (h.length === 6) {
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const m = input.match(/(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  return FALLBACK_RGB;
};

export const rgbaWithAlpha = (rgb: RGB, alpha: number): string =>
  `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;

export interface BioModalTheme {
  /** Raw accent input (hex/rgb), used as-is for borders / icon fills. */
  accent: string;
  /** Soft (alpha 0.18) — chip fills, perks pills. */
  accentSoft: string;
  /** Mid (alpha 0.45) — borders, ring colors, focus state. */
  accentMid: string;
  /** Strong (alpha 0.55) — outer glows / drop-shadows. */
  accentGlow: string;
  /** White or near-black depending on luminance, for text on the CTA. */
  ctaTextColor: string;
  /** Spread onto the backdrop wrapper (className covers position/sizing). */
  backdropStyle: React.CSSProperties;
  /** Spread onto the modal shell. */
  shellStyle: React.CSSProperties;
  /** Spread onto a primary CTA button. */
  ctaStyle: React.CSSProperties;
  /** Spread onto a "secondary" / cancel button. */
  ghostStyle: React.CSSProperties;
  /** Spread onto a text input/textarea — use focusBorder() for focus events. */
  inputStyle: React.CSSProperties;
  /** Spread onto a check-chip pill (e.g. perks list bullets). */
  checkChipStyle: React.CSSProperties;
}

/**
 * Build all the styles a themed modal needs from a single accent color.
 * Returns plain CSSProperties objects so callers can spread them into
 * inline `style={{ ... }}` props alongside Tailwind classes.
 */
export const getModalTheme = (accent?: string): BioModalTheme => {
  const safeAccent = accent || '#3b82f6';
  const rgb = parseColor(safeAccent);
  const accentSoft = rgbaWithAlpha(rgb, 0.18);
  const accentMid = rgbaWithAlpha(rgb, 0.45);
  const accentGlow = rgbaWithAlpha(rgb, 0.55);
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  const ctaTextColor = luminance > 0.6 ? '#0b1020' : '#ffffff';

  return {
    accent: safeAccent,
    accentSoft,
    accentMid,
    accentGlow,
    ctaTextColor,
    backdropStyle: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    },
    shellStyle: {
      background: `radial-gradient(140% 100% at 50% -20%, ${accentSoft} 0%, transparent 55%), linear-gradient(180deg, #0b1020 0%, #0f172a 60%, #0a0f1d 100%)`,
      border: `1px solid ${accentMid}`,
      boxShadow: `0 30px 80px -20px ${accentGlow}, 0 0 0 1px ${accentSoft}`,
    },
    ctaStyle: {
      backgroundColor: safeAccent,
      color: ctaTextColor,
      boxShadow: `0 8px 24px -6px ${accentGlow}, 0 0 0 1px ${accentMid}`,
    },
    ghostStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: `1px solid ${accentSoft}`,
      color: 'rgba(255, 255, 255, 0.85)',
    },
    inputStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      border: `1px solid ${accentSoft}`,
      color: '#ffffff',
    },
    checkChipStyle: {
      backgroundColor: accentSoft,
      boxShadow: `inset 0 0 0 1px ${accentMid}`,
    },
  };
};

/** Helper for input focus/blur handlers — keeps the accent border. */
export const focusBorder = (theme: BioModalTheme) => ({
  onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = theme.accentMid;
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = theme.accentSoft;
  },
});
